import React from 'react';
import { 
  ClockIcon, 
  EyeIcon, 
  CheckCircleIcon, 
  XCircleIcon,
  ArchiveBoxIcon,
  DocumentIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { WorkflowState, WORKFLOW_STATES } from '../../types/pim';

interface WorkflowStatusBadgeProps {
  state: WorkflowState;
  size?: 'sm' | 'md' | 'lg';
  showIcon?: boolean;
  showLabel?: boolean;
  className?: string;
}

const getStateConfig = (state: WorkflowState) => {
  return WORKFLOW_STATES.find(s => s.value === state) || WORKFLOW_STATES[0];
};

const getStateIcon = (state: WorkflowState) => {
  const iconProps = { className: "h-4 w-4" };
  
  switch (state) {
    case 'draft':
      return <DocumentIcon {...iconProps} />;
    case 'pending_review':
      return <ClockIcon {...iconProps} />;
    case 'in_review':
      return <EyeIcon {...iconProps} />;
    case 'approved':
      return <CheckCircleIcon {...iconProps} />;
    case 'published':
      return <CheckCircleIcon {...iconProps} />;
    case 'rejected':
      return <XCircleIcon {...iconProps} />;
    case 'archived':
      return <ArchiveBoxIcon {...iconProps} />;
    default:
      return <ExclamationTriangleIcon {...iconProps} />;
  }
};

const getColorClasses = (color: string, size: string) => {
  const baseClasses = {
    sm: 'px-2 py-1 text-xs',
    md: 'px-3 py-1 text-sm', 
    lg: 'px-4 py-2 text-base'
  };

  const colorClasses = {
    gray: 'bg-gray-100 text-gray-800 border-gray-200',
    yellow: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    blue: 'bg-blue-100 text-blue-800 border-blue-200',
    green: 'bg-green-100 text-green-800 border-green-200',
    emerald: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    red: 'bg-red-100 text-red-800 border-red-200',
    slate: 'bg-slate-100 text-slate-800 border-slate-200'
  };

  return `${baseClasses[size as keyof typeof baseClasses]} ${colorClasses[color as keyof typeof colorClasses]}`;
};

export const WorkflowStatusBadge: React.FC<WorkflowStatusBadgeProps> = ({
  state,
  size = 'md',
  showIcon = true,
  showLabel = true,
  className = ''
}) => {
  const config = getStateConfig(state);
  const icon = showIcon ? getStateIcon(state) : null;
  
  return (
    <span 
      className={`
        inline-flex items-center font-medium rounded-full border
        ${getColorClasses(config.color, size)}
        ${className}
      `}
      title={config.description}
    >
      {icon && (
        <span className={showLabel ? 'mr-1.5' : ''}>
          {icon}
        </span>
      )}
      {showLabel && config.label}
    </span>
  );
};

export default WorkflowStatusBadge;